import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';

const badgeVariants = cva(
    'inline-flex items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    {
        variants: {
            variant: {
                // DEC-020: el badge `default` es un DESTAQUE (no dominante) → usa el color de
                // acento de marca, no primary.
                default:
                    'border-transparent bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent/80',
                secondary:
                    'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
                destructive:
                    'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
                // DEC-021: badges de ESTADO con el patrón suave del mock (`.chip-*` de
                // components.css): fondo propio + borde propio + texto fuerte. Los tonos salen de
                // los tokens `-soft`, no de opacidades: `bg-success/10` compone sobre el sólido y
                // ensucia el color (el chip SK salía gris verdoso en vez de menta).
                success: 'border-success-soft-border bg-success-soft text-success',
                warning: 'border-warning-soft-border bg-warning-soft text-warning',
                info: 'border-info-soft-border bg-info-soft text-info',
                danger: 'border-danger-soft-border bg-danger-soft text-danger',
                muted: 'border-border bg-muted text-muted-foreground',
                outline: 'text-foreground',
            },
            size: {
                default: 'px-2.5 py-0.5 text-xs font-semibold',
                // `.chip` del mock en su uso denso (`style="padding:1px 5px"` + 9.5px): el tamaño
                // por defecto mide 22px de alto y no cabe en una fila de 28px.
                xs: 'px-[5px] py-px text-[9.5px] font-medium leading-[1.35]',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
    return <div className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

export { Badge, badgeVariants };
